import axios from "axios";
import { API_BASE as SBIN_API_BASE } from "./base";

const API = SBIN_API_BASE;

const authHeaders = (token) => {
  const authToken = token || null;
  return authToken
    ? {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      }
    : { "Content-Type": "application/json" };
};

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params || {}).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

export async function getAccountingAccess(token, params = {}) {
  const res = await axios.get(`${API}/accounting/access`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function getMyAccountingAssignments(token) {
  const res = await axios.get(`${API}/accounting/me/assignments`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function getAccountingBookingStatus(token) {
  const res = await axios.get(`${API}/accounting/booking-status`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function getAccountingProfile(token, params = {}) {
  const res = await axios.get(`${API}/accounting/profile`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function updateAccountingProfile(token, payload) {
  const res = await axios.put(`${API}/accounting/profile`, payload, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function getAccountingChecklist(token, params = {}) {
  const res = await axios.get(`${API}/accounting/checklist`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function getAccountingDocuments(token, params = {}) {
  const res = await axios.get(`${API}/accounting/documents`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function getAccountingDocumentRequests(token, params = {}) {
  const res = await axios.get(`${API}/accounting/requests`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function saveAccountingDocumentRequestAnswers(token, requestId, payload) {
  const res = await axios.post(`${API}/accounting/requests/${requestId}/answers`, payload, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function getAccountingActivity(token, params = {}) {
  const res = await axios.get(`${API}/accounting/activity`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function getAccountingCraAccess(token, params = {}) {
  const res = await axios.get(`${API}/accounting/cra-access`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function updateAccountingCraAccessChecklist(token, payload) {
  const res = await axios.put(`${API}/accounting/cra-access/checklist`, payload, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function updateAccountingCraAccessAuthorization(token, payload) {
  const res = await axios.put(`${API}/accounting/cra-access/authorization`, payload, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function updateAccountingCraAccessNotes(token, payload) {
  const res = await axios.put(`${API}/accounting/cra-access/notes`, payload, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function listAccountingCraAccessAudit(token, params = {}) {
  const res = await axios.get(`${API}/accounting/cra-access/audit`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function listAccountingReviewCraAccess(token, params = {}) {
  const res = await axios.get(`${API}/accounting/review/cra-access`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function getAccountingReviewSummary(token) {
  const res = await axios.get(`${API}/accounting/review/summary`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function listAccountingAccountants(token) {
  const res = await axios.get(`${API}/accounting/review/accountants`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function createAccountingAccountant(token, payload) {
  const res = await axios.post(`${API}/accounting/review/accountants`, payload, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function sendAccountingAccountantSetupLink(token, accountantUserId) {
  const res = await axios.post(
    `${API}/accounting/review/accountants/${accountantUserId}/setup-link`,
    {},
    {
      headers: authHeaders(token),
    }
  );
  return res.data;
}

export async function getAccountingAccountant(token, accountantUserId) {
  const res = await axios.get(`${API}/accounting/review/accountants/${accountantUserId}`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function assignAccountingAccountantSalon(token, accountantUserId, payload) {
  const res = await axios.post(
    `${API}/accounting/review/accountants/${accountantUserId}/assignments`,
    payload,
    {
      headers: authHeaders(token),
    }
  );
  return res.data;
}

export async function revokeAccountingAccountantSalon(
  token,
  accountantUserId,
  assignmentId,
  payload
) {
  const res = await axios.post(
    `${API}/accounting/review/accountants/${accountantUserId}/assignments/${assignmentId}/revoke`,
    payload,
    {
      headers: authHeaders(token),
    }
  );
  return res.data;
}

export async function getAccountingStripePriceAudit(token) {
  const res = await axios.get(`${API}/accounting/review/stripe-price-audit`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function getAccountingReviewRequestTemplates(token) {
  const res = await axios.get(`${API}/accounting/review/request-templates`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function getAccountingSourceData(token, params = {}) {
  const res = await axios.get(`${API}/accounting/source-data`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function downloadAccountingSourceDataExport(token, params = {}) {
  const res = await axios.get(`${API}/accounting/source-data/export`, {
    headers: authHeaders(token),
    params: cleanParams(params),
    responseType: "blob",
  });
  return res;
}

export async function createAccountingDocumentUploadUrl(token, payload) {
  const res = await axios.post(`${API}/accounting/documents/upload-url`, payload, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function completeAccountingDocumentUpload(token, documentId) {
  const res = await axios.post(
    `${API}/accounting/documents/complete-upload`,
    { document_id: documentId },
    {
      headers: authHeaders(token),
    }
  );
  return res.data;
}

export async function deleteAccountingDocument(token, documentId) {
  const res = await axios.delete(`${API}/accounting/documents/${documentId}`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function listAccountingReviewDocuments(token, params = {}) {
  const res = await axios.get(`${API}/accounting/review/documents`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function listAccountingReviewDocumentRequests(token, params = {}) {
  const res = await axios.get(`${API}/accounting/review/requests`, {
    headers: authHeaders(token),
    params: cleanParams(params),
  });
  return res.data;
}

export async function createAccountingReviewDocumentRequest(token, payload) {
  const res = await axios.post(`${API}/accounting/review/requests`, payload, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function sendAccountingReviewDocumentRequestReminder(token, requestId) {
  const res = await axios.post(
    `${API}/accounting/review/requests/${requestId}/reminder`,
    {},
    {
      headers: authHeaders(token),
    }
  );
  return res.data;
}

export async function updateAccountingReviewDocumentRequestFollowup(token, requestId, payload) {
  const res = await axios.patch(
    `${API}/accounting/review/requests/${requestId}/followup`,
    payload,
    {
      headers: authHeaders(token),
    }
  );
  return res.data;
}

export async function updateAccountingReviewDocumentRequestAssignment(token, requestId, payload) {
  const res = await axios.patch(
    `${API}/accounting/review/requests/${requestId}/assignment`,
    payload,
    {
      headers: authHeaders(token),
    }
  );
  return res.data;
}

export async function createAccountingReviewDocumentRequestsBulk(token, payload) {
  const res = await axios.post(`${API}/accounting/review/requests/bulk`, payload, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function updateAccountingReviewDocument(token, documentId, payload) {
  const res = await axios.patch(
    `${API}/accounting/review/documents/${documentId}`,
    payload,
    {
      headers: authHeaders(token),
    }
  );
  return res.data;
}

export async function getAccountingReviewDocumentDownloadUrl(token, documentId) {
  const res = await axios.get(
    `${API}/accounting/review/documents/${documentId}/download-url`,
    {
      headers: authHeaders(token),
    }
  );
  return res.data;
}
